import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text'
import AlertBanner from 'core-system/AlertBanner'
import Button from 'core-system/Button'
import { AppState } from 'redux/config/store'
import { reimbursementsActions } from 'redux/reimbursements/reimbursementsSlice'
import EmployeeService from 'redux/employee/employeeService'
import { SelectItemProps } from 'core-system/Dropdown'
import ReimbursementsTable, {
  ReimbursementRowData,
} from './shared/ReimbursementsTable'
import SubmitReimbursementCard from './shared/SubmitReimbursementCard'
import GuidelinesCard from './shared/GuidelinesCard'
import ReceiptReviewModal, {
  ProcessedReceipt,
} from './shared/ReceiptReviewModal'
import ApproveEligibleReimbursementsModal from './shared/ApproveEligibleReimbursementsModal'
import {
  autoApproveEligibleReimbursementsForEmployer,
  getReimbursementAdminCounts,
  ReimbursementAdminStatusFilterOption,
  uploadAndProcessReceiptForEmployee,
} from '../utils'
import Card from 'core-system/Card'
import FlexContainer from 'core-system/FlexContainer'
import {
  TableManipulationTopContainer,
  TableManipulationItem,
} from 'core-system/Table'
import { searchRows } from 'core-system/Table/helperFunctions'
import palette from 'core-system/Themes/palette'
import SearchInput from 'core-system/SearchInput'

const Container = styled.div`
  padding: 2rem;
`

const GridContainer = styled(FlexContainer)`
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    flex-direction: column;
  }
`

const GridCard = styled(Card)`
  flex: 1;
  min-width: 300px;
  padding: 1.5rem;
`

const CardTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const TableSection = styled.div`
  margin-top: 2rem;
`

const TableHeaderRow = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const TableHeader = styled(Text)`
  font-weight: 600;
`

const FilterTableContainer = styled(TableManipulationTopContainer)`
  margin-bottom: 1rem;
  border: 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    border: 1px solid ${({ theme }) => theme.palette.grey.grey300};
    padding: 1rem;
  }
`

const FilterLabelSpan = styled(Text)`
  margin-left: 0.5rem;
`

const FilterAndSearchRow = styled(FlexContainer)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

type StatusFilterOption = ReimbursementAdminStatusFilterOption

const statusFilterOptions: { value: StatusFilterOption; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'CREATED', label: 'Under Review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'REJECTED', label: 'Rejected' },
]

const ReimbursementSubmissionsView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { allEmployeeSubmissions, loading } = useSelector(
    (state: AppState) => state.reimbursements
  )
  const employerId = useSelector(
    (state: AppState) => state.employer.profile?.id
  )

  const [showAlertBanner, setShowAlertBanner] = useState(false)
  const [showErrorBanner, setShowErrorBanner] = useState(false)
  const [showAutoApproveSuccessBanner, setShowAutoApproveSuccessBanner] =
    useState(false)
  const [showAutoApproveErrorBanner, setShowAutoApproveErrorBanner] =
    useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>('all')
  const [autoApproving, setAutoApproving] = useState(false)
  const [showReceiptReviewModal, setShowReceiptReviewModal] = useState(false)
  const [processedReceipts, setProcessedReceipts] = useState<
    ProcessedReceipt[]
  >([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  )
  const [receiptDownloadUrl, setReceiptDownloadUrl] = useState('')
  const [receiptContentType, setReceiptContentType] = useState('')
  const [employeeNameSearch, setEmployeeNameSearch] = useState('')
  const [showApproveEligibleModal, setShowApproveEligibleModal] =
    useState(false)

  const [employeeOptions, setEmployeeOptions] = useState<SelectItemProps[]>([])
  const employeeSearchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  useEffect(() => {
    dispatch(reimbursementsActions.getAllEmployeeSubmissions())
  }, [dispatch])

  const handleEmployeeSearch = useCallback((value: string) => {
    if (employeeSearchTimer.current) clearTimeout(employeeSearchTimer.current)
    if (value.trim().length < 1) {
      setEmployeeOptions([])
      return
    }
    employeeSearchTimer.current = setTimeout(() => {
      EmployeeService.searchEmployees(value.trim()).then((response) => {
        setEmployeeOptions(
          (response.data || []).map((employee: { id: string; name: string }) => ({
            id: employee.id,
            text: employee.name,
          }))
        )
      })
    }, 250)
  }, [])

  const handleUpload = async (files: File[], employeeId?: string) => {
    if (!employeeId) {
      setShowErrorBanner(true)
      return
    }
    const response = await uploadAndProcessReceiptForEmployee(
      files[0],
      employeeId
    )
    if (response.status === 'success' && 'receipts' in response) {
      setShowErrorBanner(false)
      setProcessedReceipts(response.receipts)
      setSelectedEmployeeId(employeeId)
      setReceiptDownloadUrl(response.downloadUrl)
      setReceiptContentType(response.contentType)
      setShowReceiptReviewModal(true)
    } else {
      setShowAlertBanner(false)
      setShowErrorBanner(true)
    }
  }

  const handleRowClick = useCallback(
    (id: string) => {
      navigate(`/reimbursement-submissions/${id}`)
    },
    [navigate]
  )

  const handleAutoApproveEligible = useCallback(async () => {
    if (!employerId) {
      setShowAutoApproveSuccessBanner(false)
      setShowAutoApproveErrorBanner(true)
      return
    }

    setAutoApproving(true)
    const result =
      await autoApproveEligibleReimbursementsForEmployer(employerId)
    if (result.status === 'success') {
      setShowAutoApproveErrorBanner(false)
      setShowAutoApproveSuccessBanner(true)
      dispatch(reimbursementsActions.getAllEmployeeSubmissions())
    } else {
      setShowAutoApproveSuccessBanner(false)
      setShowAutoApproveErrorBanner(true)
    }
    setAutoApproving(false)
  }, [dispatch, employerId])

  const statusCounts = useMemo(() => {
    return getReimbursementAdminCounts(allEmployeeSubmissions)
  }, [allEmployeeSubmissions])

  const tableData: ReimbursementRowData[] | null = useMemo(() => {
    if (!allEmployeeSubmissions) return null

    const mappedData = allEmployeeSubmissions.map((s) => ({
      id: s.id,
      receiptDate: s.receiptDatetime,
      submittedAt: s.createdAt,
      amount: s.amount,
      approvedAmount: s.approvedAmount,
      status: s.status,
      expenseType: s.expenseType,
      employeeName: `${s.employeeFirstName} ${s.employeeLastName}`,
      note: s.note,
      receiptUrl: s.receiptUrl ?? null,
      merchantCurrency: s.merchantCurrency ?? null,
      manualReview: s.manualReview ?? false,
    }))

    const byStatus =
      statusFilter === 'all'
        ? mappedData
        : mappedData.filter((row) => row.status === statusFilter)

    return searchRows(byStatus, ['employeeName'], employeeNameSearch)
  }, [allEmployeeSubmissions, statusFilter, employeeNameSearch])

  return (
    <Container>
      {showAlertBanner && (
        <AlertBanner
          variant='success'
          message='Your claims were created successfully!'
          closeFn={() => setShowAlertBanner(false)}
          marginBottom='1rem'
        />
      )}
      {showErrorBanner && (
        <AlertBanner
          variant='redAlert'
          message='There was an issue uploading your receipt. Make sure this is an accepted receipt type and that you have not previously uploaded this receipt for reimbursement.'
          closeFn={() => setShowErrorBanner(false)}
          marginBottom='1rem'
        />
      )}
      {showAutoApproveSuccessBanner && (
        <AlertBanner
          variant='success'
          message='Sending eligible reimbursements for processing!'
          closeFn={() => setShowAutoApproveSuccessBanner(false)}
          marginBottom='1rem'
        />
      )}
      {showAutoApproveErrorBanner && (
        <AlertBanner
          variant='redAlert'
          message='There was an issue sending eligible reimbursements for processing.'
          closeFn={() => setShowAutoApproveErrorBanner(false)}
          marginBottom='1rem'
        />
      )}

      <GridContainer>
        <GridCard>
          <CardTitle variant='h5'>Submit Reimbursement</CardTitle>
          <SubmitReimbursementCard
            onUpload={handleUpload}
            showEmployeeSelector
            employeeOptions={employeeOptions}
            onEmployeeSearch={handleEmployeeSearch}
          />
        </GridCard>

        <GridCard>
          <CardTitle variant='h5'>Reimbursement Guidelines</CardTitle>
          <GuidelinesCard />
        </GridCard>
      </GridContainer>

      <TableSection>
        <TableHeaderRow>
          <TableHeader variant='h5'>Reimbursement Submissions</TableHeader>
          {/* Uncommenting out auto approve button*/}
          <Button
            disabled={autoApproving || !employerId || !allEmployeeSubmissions}
            onClick={() => setShowApproveEligibleModal(true)}
          >
            Approve Eligible Reimbursements
          </Button>
        </TableHeaderRow>
        <FilterAndSearchRow>
          <FilterTableContainer>
            {statusFilterOptions.map((option) => {
              const isActive = statusFilter === option.value
              const isDisabled =
                option.value !== 'all' && statusCounts[option.value] === 0
              return (
                <TableManipulationItem
                  key={option.value}
                  active={isActive}
                  disabled={isDisabled}
                  onClick={() => setStatusFilter(option.value)}
                >
                  {statusCounts[option.value]}
                  <FilterLabelSpan
                    variant='action4'
                    color={
                      isActive
                        ? palette.white
                        : isDisabled
                          ? palette.text.disabled
                          : palette.secondary.purple700
                    }
                  >
                    {option.label}
                  </FilterLabelSpan>
                </TableManipulationItem>
              )
            })}
          </FilterTableContainer>
          <SearchInput
            barBg='white'
            variant='small'
            placeholder='Search by employee name...'
            value={employeeNameSearch}
            width={320}
            onclearSearch={() => setEmployeeNameSearch('')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmployeeNameSearch(e.target.value)
            }
          />
        </FilterAndSearchRow>
        <ReimbursementsTable
          data={tableData}
          loading={loading}
          variant='admin'
          emptyMessage='No Reimbursement Submissions'
          onRowClick={handleRowClick}
        />
      </TableSection>

      <ApproveEligibleReimbursementsModal
        open={showApproveEligibleModal}
        onClose={() => setShowApproveEligibleModal(false)}
        onConfirm={handleAutoApproveEligible}
      />

      <ReceiptReviewModal
        open={showReceiptReviewModal}
        onClose={() => setShowReceiptReviewModal(false)}
        receipts={processedReceipts}
        variant='admin'
        employeeId={selectedEmployeeId ?? undefined}
        downloadUrl={receiptDownloadUrl}
        contentType={receiptContentType}
        onSubmitSuccess={() => {
          setShowAlertBanner(true)
          setShowErrorBanner(false)
          dispatch(reimbursementsActions.getAllEmployeeSubmissions())
        }}
        onSubmitError={() => {
          setShowAlertBanner(false)
          setShowErrorBanner(true)
        }}
      />
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ReimbursementSubmissionsView.displayName = 'ReimbursementSubmissionsView'
}

export default ReimbursementSubmissionsView
